/**
 * First-frame JPEG thumbnail from a video URL (object URL or remote).
 * Offscreen `<video>` + `<canvas>` — not appended to the DOM.
 */

function cleanupVideo (video: HTMLVideoElement): void {
  try {
    video.removeAttribute('src')
    video.load()
  } catch {
    /* ignore */
  }
}

/**
 * 1. Create `<video>` (offscreen)
 * 2. `video.src = mediaUrl`
 * 3. `video.currentTime = 0` (after metadata)
 * 4. Wait for `seeked`
 * 5. `<canvas>` sized to `video.videoWidth` × `video.videoHeight`
 * 6. 2D context
 * 7. `drawImage(video, 0, 0, w, h)`
 * 8. `canvas.toBlob` → JPEG
 * 9. Resolve with `Blob`
 */
export function extractThumbnailFromVideo (mediaUrl: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    video.src = mediaUrl

    let settled = false
    let fallbackId: number | undefined

    const clearFallback = () => {
      if (fallbackId != null) {
        window.clearTimeout(fallbackId)
        fallbackId = undefined
      }
    }

    const fail = (err: Error) => {
      if (settled) return
      settled = true
      clearFallback()
      cleanupVideo(video)
      reject(err)
    }
    const ok = (blob: Blob) => {
      if (settled) return
      settled = true
      clearFallback()
      cleanupVideo(video)
      resolve(blob)
    }

    const captureFrame = () => {
      const w = video.videoWidth
      const h = video.videoHeight
      if (!w || !h) {
        fail(new Error('Video has no decoded dimensions yet'))
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        fail(new Error('Could not get canvas 2D context'))
        return
      }

      ctx.drawImage(video, 0, 0, w, h)

      canvas.toBlob(
        (blob) => {
          if (blob) ok(blob)
          else fail(new Error('canvas.toBlob returned null'))
        },
        'image/jpeg',
        0.85
      )
    }

    const onSeeked = () => {
      try {
        captureFrame()
      } catch (e) {
        fail(e instanceof Error ? e : new Error(String(e)))
      }
    }

    video.addEventListener(
      'error',
      () => {
        fail(new Error('Video failed to load for thumbnail extraction'))
      },
      { once: true }
    )

    // After first decoded frame / dimensions, seek to t=0 then wait `seeked`
    video.addEventListener(
      'loadeddata',
      () => {
        video.addEventListener('seeked', onSeeked, { once: true })
        try {
          video.currentTime = 0
        } catch (e) {
          fail(e instanceof Error ? e : new Error(String(e)))
        }
      },
      { once: true }
    )

    // If `seeked` never fires (some UAs when already at t=0), still try capture once dimensions exist
    fallbackId = window.setTimeout(() => {
      if (settled) return
      try {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
          captureFrame()
        } else {
          fail(new Error('Thumbnail extraction timed out'))
        }
      } catch (e) {
        fail(e instanceof Error ? e : new Error(String(e)))
      }
    }, 4000)
  })
}
